import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { 
  Paper, 
  Box, 
  Typography, 
  Divider, 
  IconButton, 
  Tooltip, 
  Table, 
  TableContainer, 
  TableBody, 
  TableRow, 
  TableCell,
  Button,
  Select,
  MenuItem,
  CircularProgress,
  useTheme,
  TextField,
  InputAdornment
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';
import WarningIcon from '@mui/icons-material/Warning';
import CampaignTableHeader from './CampaignTableHeader';
import { extractDomainIdentifier } from './utils/campaignGrouping';
import CampaignTableRow from './CampaignTableRow';
import CampaignGroupRow from './CampaignGroupRow';
import DomainComparisonSummary from './DomainComparisonSummary';
import { groupCampaignsByTitle, sortGroupedCampaigns } from './utils/campaignGrouping';

/**
 * Component for displaying campaign data in a table
 */
const CampaignTable = ({
  combinedData,
  loading,
  isSubid3View,
  selectedSubid2,
  showHourlyHint,
  platformFilter,
  sortField,
  sortDirection,
  rowsPerPage,
  page,
  totalItems,
  handleSortChange,
  handleRefresh,
  handleDownloadCSV,
  handleDrillDown,
  getTaboolaCampaignData,
  isTaboolaCampaign,
  getFacebookCampaignData,
  isFacebookCampaign,
  openBudgetDialog,
  toggleCampaignStatus,
  handleBlacklist,
  handlePageChange,
  handleRowsPerPageChange,
  isBlacklisted,
  allDataFetched,
  facebookCampaigns,
  channelWarnings
}) => {
  // Get the theme instance
  const theme = useTheme();
  
  // Add state for table search
  const [tableSearchText, setTableSearchText] = useState('');
  // Add debounce state to prevent excessive filtering
  const [debouncedSearchText, setDebouncedSearchText] = useState('');
  // Add state for warnings filter
  const [showWarningsOnly, setShowWarningsOnly] = useState(false);
  // Add state for expanding all groups when sorting
  const [allGroupsExpanded, setAllGroupsExpanded] = useState(true);

  // Helper function to check if a campaign has any warnings
  const campaignHasWarning = useCallback((campaign) => {
    const cost = parseFloat(campaign.cost || 0);
    const revenue = parseFloat(campaign.revenue || 0);

    // Check no-revenue warning
    if (cost > 5 && revenue === 0) return true;

    // Check channel warnings
    if (channelWarnings) {
      const domain = extractDomainIdentifier(campaign.campaign_name);
      const channelId = channelWarnings.campaign_channels?.[campaign.campaign_id];
      const isKnowledgia = domain?.toLowerCase() === 'knowledgia';

      if (isKnowledgia && !channelId) return true;
      if (channelId && channelWarnings.duplicate_channels?.[channelId]) return true;
    }

    return false;
  }, [channelWarnings]);

  // Use useRef for debounce timeout to avoid global variables
  const debounceTimeout = useRef(null);
  
  // Create search index for better performance
  const searchIndex = useMemo(() => {
    if (!combinedData || !combinedData.campaigns) return new Map();
    
    const index = new Map();
    
    combinedData.campaigns.forEach(campaign => {
      const searchableText = [
        campaign.campaign_id || '',
        campaign.campaign_name || '',
        ...(isSubid3View ? [campaign.site_name || ''] : [])
      ].join(' ').toLowerCase();
      
      index.set(campaign.campaign_id, searchableText);
    });
    
    return index;
  }, [combinedData, isSubid3View]);
  
  // Optimized debounce handler
  const handleTableSearchChange = useCallback((e) => {
    const searchValue = e.target.value;
    setTableSearchText(searchValue);
    
    // Clear previous timeout
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }
    
    // Set debounced value after delay
    debounceTimeout.current = setTimeout(() => {
      setDebouncedSearchText(searchValue);
    }, 250); // Reduced from 300ms to 250ms for better responsiveness
  }, []);
  
  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    };
  }, []);
  
  // Optimized filtered campaigns with better performance
  const filteredCampaigns = useMemo(() => {
    if (!combinedData || !combinedData.campaigns) return [];

    let filtered = combinedData.campaigns;

    // DEBUG: Check if campaign exists before filtering
    const debugCampaignBeforeFilter = filtered.find(c => c.campaign_id === '120234812263440276');
    if (debugCampaignBeforeFilter) {
      console.log('🔍 DEBUG TABLE: Campaign 120234812263440276 exists BEFORE filters:', {
        platform: debugCampaignBeforeFilter.platform,
        currentPlatformFilter: platformFilter
      });
    }

    // Platform filter first (usually smaller subset)
    if (platformFilter !== 'all') {
      filtered = filtered.filter(campaign => campaign.platform === platformFilter);

      // DEBUG: Check if campaign survived platform filter
      const debugCampaignAfterPlatform = filtered.find(c => c.campaign_id === '120234812263440276');
      if (!debugCampaignAfterPlatform && debugCampaignBeforeFilter) {
        console.log('🔍 DEBUG TABLE: Campaign 120234812263440276 FILTERED OUT by platform filter!', {
          campaignPlatform: debugCampaignBeforeFilter.platform,
          filterValue: platformFilter
        });
      }
    }

    // Search filter with optimized search using index
    if (debouncedSearchText && debouncedSearchText.length > 0) {
      const searchLower = debouncedSearchText.toLowerCase();

      const beforeSearchFilter = filtered.find(c => c.campaign_id === '120234812263440276');

      filtered = filtered.filter(campaign => {
        const searchableText = searchIndex.get(campaign.campaign_id);
        return searchableText && searchableText.includes(searchLower);
      });

      // DEBUG: Check if campaign survived search filter
      const afterSearchFilter = filtered.find(c => c.campaign_id === '120234812263440276');
      if (!afterSearchFilter && beforeSearchFilter) {
        console.log('🔍 DEBUG TABLE: Campaign 120234812263440276 FILTERED OUT by search filter!', {
          searchText: debouncedSearchText
        });
      }
    }

    // DEBUG: Final check
    const debugCampaignFinal = filtered.find(c => c.campaign_id === '120234812263440276');
    if (debugCampaignFinal) {
      console.log('🔍 DEBUG TABLE: Campaign 120234812263440276 IN FINAL filtered list');
    } else if (debugCampaignBeforeFilter) {
      console.log('🔍 DEBUG TABLE: Campaign 120234812263440276 NOT in final filtered list');
    }

    // Warnings filter
    if (showWarningsOnly) {
      filtered = filtered.filter(campaign => campaignHasWarning(campaign));
    }

    return filtered;
  }, [combinedData, platformFilter, debouncedSearchText, searchIndex, showWarningsOnly, campaignHasWarning]);
  
  // Optimized platform campaigns calculation
  const platformCampaigns = useMemo(() => {
    if (!combinedData || !combinedData.campaigns) {
      return { taboola: [], facebook: [] };
    }
    
    // Use reduce for single-pass filtering
    return combinedData.campaigns.reduce((acc, campaign) => {
      if (campaign.platform === 'taboola') {
        acc.taboola.push(campaign);
      } else if (campaign.platform === 'facebook') {
        acc.facebook.push(campaign);
      }
      return acc;
    }, { taboola: [], facebook: [] });
  }, [combinedData]);
  
  // Get platform-specific campaigns regardless of search filter
  const getAllPlatformCampaigns = useCallback((platform) => {
    return platformCampaigns[platform] || [];
  }, [platformCampaigns]);
  
  // Simple direct drill-down function that calls the parent handler
  const onDrillDown = useCallback((campaignId) => {
    console.log('Drill-down requested for campaign:', campaignId);
    handleDrillDown(campaignId);
  }, [handleDrillDown]);
  
  // Optimized sorting and final campaign list
  const sortedCampaigns = useMemo(() => {
    if (!filteredCampaigns || filteredCampaigns.length === 0) {
      return [];
    }
    
    // Create a copy to avoid mutating the original array
    const campaignsCopy = [...filteredCampaigns];
    
    // Pre-compute sort values for better performance
    const sortValues = new Map();
    
    campaignsCopy.forEach(campaign => {
      let value;
      
      // Handle specific field types - ensure consistent value extraction
      switch (sortField) {
        case 'campaign_id':
          value = campaign.campaign_id || campaign.subid_3 || '';
          break;
        case 'impressions':
          value = parseInt(campaign.impressions || 0, 10);
          break;
        case 'ctr':
          // Handle CVR as a percentage and clean any string formatting
          let ctr = campaign.ctr;
          if (typeof ctr === 'string') {
            ctr = ctr.replace('%', '');
          }
          value = parseFloat(ctr || 0);
          break;
        case 'clicks':
          value = parseInt(campaign.clicks || 0, 10);
          break;
        case 'revenue':
          value = parseFloat(campaign.revenue || 0);
          break;
        case 'cost':
          value = parseFloat(campaign.cost || 0);
          break;
        case 'profit':
          value = parseFloat(campaign.profit || 0);
          break;
        case 'roas':
          value = parseFloat(campaign.roas || 0);
          break;
        case 'payout':
          value = parseFloat(campaign.payout || 0);
          break;
        case 'cpc':
          // Calculate CPC: Cost / Clicks
          const cost = parseFloat(campaign.cost || 0);
          const clicks = parseInt(campaign.clicks || 0, 10);
          value = clicks > 0 ? cost / clicks : 0;
          break;
        case 'fb_ctr':
          // Calculate Facebook CTR: Clicks / Impressions * 100
          if (campaign.platform === 'facebook') {
            const fbImpressions = parseInt(campaign.impressions || 0, 10);
            const fbClicks = parseInt(campaign.clicks || 0, 10);
            value = fbImpressions > 0 ? (fbClicks / fbImpressions) * 100 : 0;
          } else {
            value = 0; // Non-Facebook campaigns don't have CTR
          }
          break;
        default:
          value = campaign[sortField] || '';
      }
      
      // Store the extracted value for fast access during sort
      sortValues.set(campaign, value);
    });
    
    // Sort the campaigns based on cached values
    campaignsCopy.sort((a, b) => {
      const valA = sortValues.get(a);
      const valB = sortValues.get(b);
      
      // Handle null/undefined values
      if (valA == null && valB == null) return 0;
      if (valA == null) return sortDirection === 'asc' ? -1 : 1;
      if (valB == null) return sortDirection === 'asc' ? 1 : -1;
      
      // For string comparison
      if (typeof valA === 'string' && typeof valB === 'string') {
        return sortDirection === 'asc' 
          ? valA.localeCompare(valB) 
          : valB.localeCompare(valA);
      }
      
      // For numeric comparison
      return sortDirection === 'asc' ? valA - valB : valB - valA;
    });
    
    return campaignsCopy;
  }, [filteredCampaigns, sortField, sortDirection]);

  // Group campaigns by title (text after colon) - only in main view, not subid3 view
  const groupedCampaigns = useMemo(() => {
    if (!sortedCampaigns || sortedCampaigns.length === 0 || isSubid3View) {
      return null; // No grouping in subid3 view or if no campaigns
    }

    // Group campaigns by title
    const grouped = groupCampaignsByTitle(sortedCampaigns);

    // Sort the grouped data by the current sort field
    return sortGroupedCampaigns(grouped, sortField, sortDirection);
  }, [sortedCampaigns, isSubid3View, sortField, sortDirection]);

  // Optimized totals calculation
  const totalsData = useMemo(() => {
    if (!sortedCampaigns || sortedCampaigns.length === 0) {
      return null;
    }

    // Single-pass calculation for better performance
    // Separate totals for visits, bought clicks, and demand clicks to handle mixed campaign types
    const totals = sortedCampaigns.reduce((acc, campaign) => {
      // For visits: use demand_visits for Facebook, impressions for others
      if (campaign.platform === 'facebook') {
        acc.visits += parseInt(campaign.demand_visits || 0, 10);
        acc.demandClicks += parseInt(campaign.demand_clicks || 0, 10);
        acc.boughtClicks += parseInt(campaign.clicks || 0, 10); // Facebook ad clicks
        acc.fbImpressions += parseInt(campaign.impressions || 0, 10); // Facebook impressions

        // Calculate budget from facebookCampaigns (object keyed by campaign ID)
        if (facebookCampaigns && typeof facebookCampaigns === 'object') {
          const fbCampaign = facebookCampaigns[campaign.campaign_id];
          if (fbCampaign) {
            let budgetValue = fbCampaign.daily_budget;
            if (budgetValue === undefined || budgetValue === null) {
              if (fbCampaign.budget !== undefined && fbCampaign.budget !== null) {
                budgetValue = fbCampaign.budget;
              } else if (fbCampaign.lifetime_budget !== undefined && fbCampaign.lifetime_budget !== null) {
                budgetValue = parseFloat(fbCampaign.lifetime_budget) / 30;
              }
            }
            if (budgetValue !== undefined && budgetValue !== null && !isNaN(parseFloat(budgetValue))) {
              acc.totalBudget += parseFloat(budgetValue);
            }
          }
        }
      } else {
        acc.visits += parseInt(campaign.impressions || 0, 10);
        acc.demandClicks += parseInt(campaign.clicks || 0, 10); // Same as clicks for non-FB
        acc.boughtClicks += parseInt(campaign.clicks || 0, 10); // Same as visits for non-FB
      }
      acc.revenue += parseFloat(campaign.revenue || 0);
      acc.cost += parseFloat(campaign.cost || 0);
      return acc;
    }, { visits: 0, demandClicks: 0, boughtClicks: 0, fbImpressions: 0, revenue: 0, cost: 0, totalBudget: 0 });

    const profit = parseFloat((totals.revenue - totals.cost).toFixed(2));
    const roas = totals.cost > 0 ? parseFloat((totals.revenue / totals.cost).toFixed(2)) : 0;
    const payout = totals.boughtClicks > 0 ? parseFloat((totals.revenue / totals.boughtClicks).toFixed(2)) : 0;
    const fbCtr = totals.fbImpressions > 0 ? ((totals.boughtClicks / totals.fbImpressions) * 100).toFixed(2) + '%' : '0.00%';
    const cvr = totals.boughtClicks > 0 ? ((totals.demandClicks / totals.boughtClicks) * 100).toFixed(2) + '%' : '0.00%';
    const trafficLost = totals.boughtClicks > 0 ? ((totals.boughtClicks - totals.visits) / totals.boughtClicks) * 100 : 0;
    const trafficLostFormatted = trafficLost.toFixed(2) + '%';
    const cpc = totals.boughtClicks > 0 ? parseFloat((totals.cost / totals.boughtClicks).toFixed(2)) : 0;

    return {
      count: sortedCampaigns.length,
      visits: totals.visits.toLocaleString(),
      boughtClicks: totals.boughtClicks.toLocaleString(),
      demandClicks: totals.demandClicks.toLocaleString(),
      fbCtr,
      cvr,
      revenue: '$' + totals.revenue.toFixed(2),
      cost: '$' + totals.cost.toFixed(2),
      profit: '$' + profit.toFixed(2),
      profitValue: profit,
      roas: roas.toFixed(2),
      roasValue: roas,
      trafficLost: trafficLostFormatted,
      trafficLostValue: trafficLost,
      payout: '$' + payout.toFixed(2),
      cpc: '$' + cpc.toFixed(2),
      totalBudget: '$' + totals.totalBudget.toFixed(2)
    };
  }, [sortedCampaigns, facebookCampaigns]);
  
  // Render a row for the table totals
  const renderTotalsRow = useMemo(() => {
    if (!totalsData) return null;

    const colSpan = isSubid3View ? 1 : 1;

    // Helper for hiding columns on mobile
    const hiddenOnMobile = { display: { xs: 'none', md: 'table-cell' } };

    return (
      <TableRow sx={{ backgroundColor: theme.palette.grey[50], fontWeight: 'bold' }}>
        <TableCell colSpan={colSpan} sx={{ fontWeight: 'bold' }}>
          Totals ({totalsData.count} {isSubid3View ? 'sites' : 'campaigns'})
        </TableCell>
        <TableCell align="right" sx={{ fontWeight: 'bold', ...hiddenOnMobile }}>{totalsData.boughtClicks}</TableCell>
        {/* Visits and Traffic Lost removed */}
        <TableCell align="right" sx={{ fontWeight: 'bold', ...hiddenOnMobile }}>{totalsData.fbCtr}</TableCell>
        <TableCell align="right" sx={{ fontWeight: 'bold', ...hiddenOnMobile }}>{totalsData.cvr}</TableCell>
        <TableCell align="right" sx={{ fontWeight: 'bold', ...hiddenOnMobile }}>{totalsData.demandClicks || '0'}</TableCell>
        <TableCell align="right" sx={{ fontWeight: 'bold' }}>{totalsData.cost}</TableCell>
        <TableCell align="right" sx={{ fontWeight: 'bold' }}>{totalsData.revenue}</TableCell>
        <TableCell align="right" sx={{ fontWeight: 'bold', ...hiddenOnMobile }}>{totalsData.payout}</TableCell>
        <TableCell align="right" sx={{ fontWeight: 'bold', ...hiddenOnMobile }}>{totalsData.cpc}</TableCell>
        <TableCell
          align="right"
          sx={{
            fontWeight: 'bold',
            color: totalsData.roasValue >= 1 ? theme.palette.success.main : theme.palette.error.main
          }}
        >
          {totalsData.roas}
        </TableCell>
        <TableCell
          align="right"
          sx={{
            fontWeight: 'bold',
            color: totalsData.profitValue >= 0 ? theme.palette.success.main : theme.palette.error.main,
            ...hiddenOnMobile
          }}
        >
          {totalsData.profit}
        </TableCell>
        {/* Add total Budget and empty Actions column when not in subid3 view */}
        {!isSubid3View && (
          <>
            <TableCell align="right" sx={{ fontWeight: 'bold', ...hiddenOnMobile }}>
              {totalsData.totalBudget}
            </TableCell>
            <TableCell />
          </>
        )}
        {/* Add empty cell for the Blacklist column in subid3 view */}
        {isSubid3View && <TableCell />}
      </TableRow>
    );
  }, [totalsData, isSubid3View, theme.palette]);

  return (
    <Paper sx={{ mb: 2, position: 'relative' }}>
      {/* Debug logging for subid2 */}
      {isSubid3View && process.env.NODE_ENV !== 'production' && console.log('CampaignTable - selectedSubid2:', selectedSubid2)}
      
      {/* Hourly hint when single day is selected */}
      {showHourlyHint && !isSubid3View && (
        <Box sx={{ p: 1, backgroundColor: '#e3f2fd', borderRadius: '4px 4px 0 0', borderBottom: '1px solid #bbdefb' }}>
          <Typography variant="caption" color="primary" sx={{ fontWeight: 'medium' }}>
            💡 Tip: Click on any campaign name to view hourly performance data for this day
          </Typography>
        </Box>
      )}
      
      {/* Loading overlay */}
      {loading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(255, 255, 255, 0.7)',
            zIndex: 10,
            borderRadius: 1
          }}
        >
          <Box sx={{ textAlign: 'center' }}>
            <CircularProgress size={40} />
            <Typography variant="body2" sx={{ mt: 1 }}>
              Loading data...
            </Typography>
          </Box>
        </Box>
      )}
      
      <Box sx={{ p: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 500, fontSize: '0.95rem' }}>
          {isSubid3View 
            ? `Sub-Campaigns (subid3) for ${selectedSubid2}`
            : 'Campaign Revenue & Cost by Campaign ID (subid2)'}
        </Typography>
        <Box>
          <Tooltip title="Refresh Data">
            <IconButton onClick={handleRefresh} disabled={loading} sx={{ mr: 0.5, padding: '4px' }}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Force Refresh (Bypass Cache)">
            <IconButton 
              onClick={() => handleRefresh(true)} 
              disabled={loading} 
              sx={{ mr: 0.5, padding: '4px', color: theme.palette.warning.main }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Download as CSV">
            <IconButton onClick={handleDownloadCSV} disabled={!combinedData.campaigns || combinedData.campaigns.length === 0} sx={{ padding: '4px' }}>
              <DownloadIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
      <Divider />
      
      {/* Add search input for table filtering */}
      <Box sx={{ p: 1.5, pt: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Search in table data..."
          value={tableSearchText}
          onChange={handleTableSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
          }}
          sx={{ flex: 1 }}
        />
        <Tooltip title={showWarningsOnly ? "Show all campaigns" : "Show only campaigns with warnings"}>
          <Button
            variant={showWarningsOnly ? "contained" : "outlined"}
            size="small"
            color="warning"
            onClick={() => setShowWarningsOnly(!showWarningsOnly)}
            startIcon={<WarningIcon />}
            sx={{
              minWidth: 'auto',
              px: 1.5,
              whiteSpace: 'nowrap'
            }}
          >
            Warnings
          </Button>
        </Tooltip>
      </Box>

      {/* Domain comparison summary - only show in main view with grouped campaigns */}
      {!isSubid3View && groupedCampaigns && (
        <Box sx={{ px: { xs: 0.5, md: 1.5 }, pb: 1 }}>
          <DomainComparisonSummary groupedCampaigns={groupedCampaigns} facebookCampaigns={facebookCampaigns} />
        </Box>
      )}

      <TableContainer sx={{ overflowX: 'hidden', maxHeight: '70vh' }}>
        <Table
          stickyHeader
          size="small"
          sx={{
            tableLayout: 'fixed',
            width: '100%',
            '& .MuiTableCell-root': {
              py: 0.35,
              px: 0.5,
              fontSize: '0.7rem',
              borderBottom: '1px solid rgba(224, 224, 224, 0.3)',
              lineHeight: 1.2,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            },
            '& .MuiTableCell-head': {
              py: 0.5,
              whiteSpace: 'nowrap',
              fontSize: '0.7rem',
              fontWeight: 600
            },
            '& .number-cell': {
              fontFamily: '"Roboto Mono", monospace',
              letterSpacing: -0.3,
              fontSize: '0.68rem'
            }
          }}
        >
          <CampaignTableHeader 
            isSubid3View={isSubid3View}
            sortField={sortField}
            sortDirection={sortDirection}
            handleSortChange={handleSortChange}
          />
          
          <TableBody>
            {combinedData.campaigns && sortedCampaigns.length > 0 ? (
              <>
                {/* Use grouped view in main view, flat view in subid3 view */}
                {groupedCampaigns ? (
                  // Grouped view - render groups and single campaigns
                  groupedCampaigns.map((item, index) => {
                    if (item.isGroup) {
                      // Render group row with expandable children
                      return (
                        <CampaignGroupRow
                          key={item.groupId}
                          group={item}
                          startIndex={index}
                          isSubid3View={isSubid3View}
                          selectedSubid2={selectedSubid2}
                          getTaboolaCampaignData={getTaboolaCampaignData}
                          isTaboolaCampaign={isTaboolaCampaign}
                          getFacebookCampaignData={getFacebookCampaignData}
                          isFacebookCampaign={isFacebookCampaign}
                          handleDrillDown={onDrillDown}
                          openBudgetDialog={openBudgetDialog}
                          toggleCampaignStatus={toggleCampaignStatus}
                          handleBlacklist={handleBlacklist}
                          loading={loading}
                          isBlacklisted={isBlacklisted}
                          facebookCampaigns={facebookCampaigns}
                          channelWarnings={channelWarnings}
                          defaultExpanded={allGroupsExpanded}
                        />
                      );
                    } else {
                      // Render single campaign row (not part of a group)
                      return (
                        <CampaignTableRow
                          key={`single-${item.campaign.campaign_id}-${index}`}
                          campaign={item.campaign}
                          index={index}
                          isSubid3View={isSubid3View}
                          selectedSubid2={selectedSubid2}
                          getTaboolaCampaignData={getTaboolaCampaignData}
                          isTaboolaCampaign={isTaboolaCampaign}
                          getFacebookCampaignData={getFacebookCampaignData}
                          isFacebookCampaign={isFacebookCampaign}
                          handleDrillDown={onDrillDown}
                          openBudgetDialog={openBudgetDialog}
                          toggleCampaignStatus={toggleCampaignStatus}
                          handleBlacklist={handleBlacklist}
                          loading={loading}
                          isBlacklisted={isBlacklisted}
                          facebookCampaigns={facebookCampaigns}
                          channelWarnings={channelWarnings}
                        />
                      );
                    }
                  })
                ) : (
                  // Flat view (subid3 view) - render all campaigns directly
                  sortedCampaigns.map((campaign, index) => (
                    <CampaignTableRow
                      key={`${campaign.campaign_id}-${index}`}
                      campaign={campaign}
                      index={index}
                      isSubid3View={isSubid3View}
                      selectedSubid2={selectedSubid2}
                      getTaboolaCampaignData={getTaboolaCampaignData}
                      isTaboolaCampaign={isTaboolaCampaign}
                      getFacebookCampaignData={getFacebookCampaignData}
                      isFacebookCampaign={isFacebookCampaign}
                      handleDrillDown={onDrillDown}
                      openBudgetDialog={openBudgetDialog}
                      toggleCampaignStatus={toggleCampaignStatus}
                      handleBlacklist={handleBlacklist}
                      loading={loading}
                      isBlacklisted={isBlacklisted}
                      facebookCampaigns={facebookCampaigns}
                      channelWarnings={channelWarnings}
                    />
                  ))
                )}

                {/* Totals Row */}
                {renderTotalsRow}
              </>
            ) : (
              <TableRow>
                <TableCell colSpan={isSubid3View ? 9 : 10} align="center">
                  {tableSearchText ? 'No matching results found' : 'No campaign data available'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      
      {combinedData.campaigns && combinedData.campaigns.length > 0 && (
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          p: 1,
          borderTop: 1,
          borderColor: 'divider'
        }}>
          <Box>
            <Typography variant="caption" color="text.secondary">
              {(() => {
                // Calculate number of filtered campaigns
                const filteredCampaignsCount = sortedCampaigns.length;
                const totalFilteredCount = tableSearchText 
                  ? `${filteredCampaignsCount} of ${combinedData.campaigns.length}` 
                  : combinedData.campaigns.length;
                
                // Add platform counts if using search
                const taboolaCount = tableSearchText ? 
                  `(${getAllPlatformCampaigns('taboola').length} Taboola)` : '';
                
                if (allDataFetched) {
                  return `Showing ${tableSearchText ? filteredCampaignsCount + ' filtered' : 'all'} ${platformFilter !== 'all' ? platformFilter + ' ' : ''}${isSubid3View ? 'sites' : 'campaigns'} ${taboolaCount}`;
                } else if (platformFilter !== 'all') {
                  return `Showing ${Math.min(filteredCampaignsCount, rowsPerPage)} of ${totalFilteredCount} ${platformFilter} campaigns`;
                } else {
                  return `Showing ${Math.min(filteredCampaignsCount, rowsPerPage)} of ${totalItems}${tableSearchText ? ' filtered' : ''} campaigns ${taboolaCount}`;
                }
              })()}
            </Typography>
          </Box>
          {!allDataFetched && (
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="caption" color="text.secondary" sx={{ mr: 1 }}>
                Rows per page:
                <Select
                  value={rowsPerPage}
                  onChange={handleRowsPerPageChange}
                  size="small"
                  sx={{ ml: 0.5, height: '24px', minWidth: 60, '& .MuiSelect-select': { py: 0.25, px: 0.75 } }}
                >
                  {[10, 25, 50, 100].map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </Typography>
              
              <Button
                variant="outlined"
                size="small"
                disabled={page === 0}
                onClick={() => handlePageChange(null, page - 1)}
                sx={{ minWidth: 'auto', mr: 0.5, py: 0.25, px: 0.75, fontSize: '0.7rem' }}
              >
                Prev
              </Button>
              <Typography variant="caption" color="text.secondary" sx={{ mx: 1 }}>
                Page {page + 1} of {Math.ceil(totalItems / rowsPerPage)}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                disabled={page >= Math.ceil(totalItems / rowsPerPage) - 1}
                onClick={() => handlePageChange(null, page + 1)}
                sx={{ minWidth: 'auto', py: 0.25, px: 0.75, fontSize: '0.7rem' }}
              >
                Next
              </Button>
            </Box>
          )}
        </Box>
      )}
    </Paper>
  );
};

export default CampaignTable; 